import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CurrencyInput,
  Field,
  Fieldset,
  Form,
  InputGroup,
  Modal,
  NumberInput,
  RadioGroupField,
} from '@payaca/ambrosia';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

import useSetProposalDeposit from '@/api/mutations/proposals/useSetProposalDeposit';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import {
  DepositValueType,
  ProposalDepositFormValues,
  proposalDepositFormSchema,
} from '@/ui/pages/proposalBuilderPage/components/proposalDepositSchema';

const VALUE_TYPE_OPTIONS: { label: string; value: DepositValueType }[] = [
  { label: 'Fixed amount', value: 'amount' },
  { label: 'Percentage', value: 'percentage' },
];

const seedValues = (
  amount: number | null,
  percentage: number | null
): ProposalDepositFormValues => ({
  deposit:
    amount != null
      ? { valueType: 'amount', amount, percentage: null }
      : { valueType: 'percentage', amount: null, percentage },
});

export type ProposalDepositModalProps = {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  /** Title for the dialogue, so it matches the action that opened it. */
  title: string;
  /** Stored fixed deposit in minor units; null when percentage-based or unset. */
  currentDepositAmount: number | null;
  /** Stored deposit percentage; null when amount-based or unset. */
  currentDepositPercentage: number | null;
};

/**
 * Editor for the deposit requested on a Proposal — a fixed amount or a
 * percentage of the total.
 */
const ProposalDepositModal: FC<ProposalDepositModalProps> = (props) => {
  const {
    isOpen,
    onClose,
    proposalId,
    title,
    currentDepositAmount,
    currentDepositPercentage,
  } = props;

  const { data: regionPreferences } = useGetMyRegionPreferences();
  const { mutate, isPending } = useSetProposalDeposit({ proposalId });

  // `values` re-seeds the form whenever the stored deposit changes (e.g. it
  // arrives after an open-via-URL-hash). It cannot see a reopen with
  // unchanged data, so the consumer keys this modal per open to remount the
  // form and discard cancelled edits.
  const formMethods = useForm<ProposalDepositFormValues>({
    values: seedValues(currentDepositAmount, currentDepositPercentage),
    resolver: zodResolver(proposalDepositFormSchema),
    mode: 'onTouched',
  });

  const handleSubmit = (formValues: ProposalDepositFormValues) => {
    const { deposit } = formValues;

    // The schema guarantees the active mode's field is set before submit
    // runs; the null-checks narrow for the @oneOf input union.
    let depositInput = null;
    if (deposit.valueType === 'amount' && deposit.amount != null) {
      depositInput = { amount: deposit.amount };
    } else if (
      deposit.valueType === 'percentage' &&
      deposit.percentage != null
    ) {
      depositInput = { percentage: deposit.percentage };
    }

    mutate({ proposalId, deposit: depositInput }, { onSuccess: onClose });
  };

  // Bypasses form validation deliberately — clearing must work even while
  // the active input is empty or invalid.
  const handleClearDeposit = () => {
    mutate({ proposalId, deposit: null }, { onSuccess: onClose });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <Form methods={formMethods} onSubmit={handleSubmit}>
        <Modal.Body>
          <Controller<ProposalDepositFormValues, 'deposit'>
            name="deposit"
            control={formMethods.control}
            render={({ field: { value, onChange, onBlur } }) => (
              <Fieldset>
                <Field name="deposit.valueType">
                  <Field.Label>Deposit type</Field.Label>
                  <RadioGroupField
                    horizontal
                    options={VALUE_TYPE_OPTIONS}
                    value={value.valueType}
                    onChange={(valueType) =>
                      // Clear the inactive input on toggle so a stale seeded
                      // value can't be submitted.
                      onChange({
                        valueType:
                          valueType === 'amount' ? 'amount' : 'percentage',
                        amount: null,
                        percentage: null,
                      })
                    }
                  />
                </Field>

                {value.valueType === 'amount' ? (
                  <Field name="deposit.amount">
                    <Field.Label>Deposit amount</Field.Label>
                    <CurrencyInput
                      value={value.amount ?? 0}
                      onChange={(amount) => onChange({ ...value, amount })}
                      onBlur={onBlur}
                      currency={regionPreferences?.currency}
                    />
                  </Field>
                ) : (
                  <Field name="deposit.percentage">
                    <Field.Label>Deposit percentage</Field.Label>
                    <InputGroup>
                      <NumberInput
                        as={InputGroup.RawInput}
                        value={value.percentage}
                        onChange={(percentage) =>
                          onChange({ ...value, percentage })
                        }
                        onBlur={onBlur}
                        format={{ maximumFractionDigits: 2 }}
                      />
                      <InputGroup.Icon name="percent-02.3" />
                    </InputGroup>
                  </Field>
                )}
              </Fieldset>
            )}
          />
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="tertiary"
              type="button"
              className="mr-auto"
              onClick={handleClearDeposit}
              disabled={isPending}
            >
              No deposit
            </Button>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isProcessing={isPending} disabled={isPending}>
              Save deposit
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ProposalDepositModal;
